import { useNavigate } from 'react-router-dom'
import { useReselector } from '@/utils/sharedHooks'
import {
  BankingDisplayState,
  selectBankingApplicationStatus,
  selectBankingDisplayState,
  selectConnectBankPath,
  selectHasConnectedBankAccount,
} from '@/features/banking/banking.selectors'
import { BankingApplicationStatus } from '@/features/banking/banking.types'
import {
  OFFER_SOURCES,
  useBankingAnalytics,
} from '@/features/banking/analytics'

export interface ConnectHeardBankItemConfig {
  header: string
  description: string
  actionText: string
  disabled: boolean
  onActionClick: () => void
}

/**
 * Picks the right TaskItem props for the connectHeardBank step based on
 * the user's banking application status and connected-account state.
 *
 * The card-level banking gate (selectBankingDisplayState) collapses
 * Pending / PendingReview / AwaitingDocuments into a single
 * 'AwaitingApplication' state. This hook deliberately reads
 * selectBankingApplicationStatus directly so that PendingReview can show a
 * disabled "Coming Soon" item while Pending / AwaitingDocuments show an
 * active "Continue" item.
 */
export const useConnectHeardBankItemConfig = (): ConnectHeardBankItemConfig => {
  const navigate = useNavigate()
  const bankingDisplayState = useReselector(selectBankingDisplayState)
  const status = useReselector(selectBankingApplicationStatus)
  const hasAnyBank = useReselector(selectHasConnectedBankAccount)
  const connectBankPath = useReselector(selectConnectBankPath)
  const { trackOfferClicked } = useBankingAnalytics()

  // Wrap a navigation handler so the offer-click event always fires before
  // navigating. Disabled variants (PendingReview "Coming Soon") use a no-op
  // and are not wrapped — clicks on disabled items don't represent intent.
  const trackThenNavigate = (path: string) => () => {
    trackOfferClicked(OFFER_SOURCES.GettingStartedChecklist)
    navigate(path)
  }

  switch (status) {
    case BankingApplicationStatus.Pending:
      return {
        header: 'Complete your Heard Banking application',
        description: 'Complete and submit your banking application.',
        actionText: 'Continue',
        disabled: false,
        onActionClick: trackThenNavigate('/banking/apply'),
      }
    case BankingApplicationStatus.AwaitingDocuments:
      return {
        header: 'Complete your Heard Banking application',
        description:
          'The banking team needs documents to verify your business.',
        actionText: 'Continue',
        disabled: false,
        onActionClick: trackThenNavigate('/banking/apply'),
      }
    case BankingApplicationStatus.PendingReview:
      return {
        header: 'Complete Heard Banking setup',
        description: 'Your application is under review',
        actionText: 'Coming Soon',
        disabled: true,
        onActionClick: () => {},
      }
  }

  // !hasAnyBank so an approved user with an external bank gets the Heard-Banking copy below.
  if (!hasAnyBank && bankingDisplayState !== BankingDisplayState.Normal) {
    // ApprovedNoAccounts or NoAccountsFallback — same item shape per spec §7.
    return {
      header: 'Connect bank account',
      description:
        'Connect your bank so Heard can automatically sync your transactions and start your bookkeeping.',
      actionText: 'Connect',
      disabled: false,
      onActionClick: trackThenNavigate(connectBankPath),
    }
  }

  // Normal — preserve today's behavior.
  return {
    header: 'Complete Heard Banking setup',
    description:
      "Now that you're approved, connect Heard banking to your bookkeeping.",
    actionText: 'Connect',
    disabled: false,
    onActionClick: trackThenNavigate('/accounts/connect'),
  }
}
